/**
 * Reading a saved inspection submission back into the form that wrote it.
 *
 * WHY THIS IS NEEDED
 * ------------------
 * `EquipmentInspectionForm` mints a `status: 'draft'` row the moment a form
 * type and an equipment are chosen, so PhotoUploader has a real UUID to key
 * against. It then holds that id in component state and never reads the row
 * back. Close the form and the row is orphaned: the work is in the database and
 * there is no path in the product that opens it again. Three such rows are in
 * production today.
 *
 * THIS IS THE INVERSE OF `buildSubmissionData` / `buildTongTruckSubmission`,
 * AND THE TWO MUST STAY EXACT INVERSES. A key present in the forward map and
 * missing here does not fail — it restores blank, and a blank field reads as
 * "the hand never filled that in" rather than as a bug. That is the silent
 * failure this whole file is exposed to, so it is pinned by a round-trip test
 * that seeds a fully-populated submission into the real component, saves, and
 * compares the payload against what went in
 * (`EquipmentInspectionForm.resume-roundtrip.test.tsx`). Add a field to either
 * direction and that test tells you about the other one.
 *
 * READS ARE snake_case HERE. This endpoint answers with `SELECT s.*` off raw
 * pg, so the row arrives with its column names, not the camelCase most of this
 * API returns. Both spellings are tolerated below anyway — the cost is one
 * `??` per field and the alternative is a class of bug this repo has shipped
 * three times in a week.
 *
 * ABSENT RESTORES AS AN EMPTY STRING, NEVER A DEFAULT. These values feed
 * controlled inputs, so `''` is "nothing recorded" and the field renders empty
 * and editable. A plausible default here would be a value the inspector never
 * entered, saved back under their signature on the next save.
 */

import { calendarDayString } from './calendar-date';

/** A submission row as the list and detail endpoints return it. */
export type InspectionRow = Record<string, any>;

/** `''` for anything absent. Never `null`, never a stand-in value. */
const str = (...candidates: unknown[]): string => {
  for (const c of candidates) {
    if (c === null || c === undefined) continue;
    const s = String(c);
    if (s.trim() !== '') return s;
  }
  return '';
};

/** Pick the first present key, tolerating snake_case and camelCase. */
const pick = (row: InspectionRow, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
  }
  return undefined;
};

/**
 * `results` back into the per-item state the checklists render from.
 *
 * ONE SHAPE ACROSS ALL FIVE FORM TYPES. Both builders key results as
 * `${sectionNumber}_${itemIndex}` with `{ result, comment }` (the Tong Truck
 * one adds a `label`, which is display text the checklist re-derives from its
 * own item list and is deliberately not restored into state). So this is the
 * one piece of the inverse that needs no per-type branch — and it is also the
 * piece carrying the actual inspection work, every answered question on the
 * form.
 *
 * A `null` result means the row was rendered but never answered. It restores as
 * `''`, which is what an unanswered row looks like to `toggleItem`, rather than
 * as a pass.
 */
export function resumeItemResults(
  row: InspectionRow,
): Record<string, { result: string; comment: string }> {
  const results = (pick(row, 'results') ?? {}) as Record<string, any>;
  const out: Record<string, { result: string; comment: string }> = {};
  for (const [key, value] of Object.entries(results)) {
    if (!value || typeof value !== 'object') continue;
    out[key] = { result: str(value.result), comment: str(value.comment) };
  }
  return out;
}

/** Deficiencies, dropping any entry with no description — same rule the form saves under. */
export function resumeDeficiencies(
  row: InspectionRow,
): { description: string; severity: 'low' | 'medium' | 'critical' }[] {
  const raw = pick(row, 'deficiencies');
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((d: any) => d && String(d.description ?? '').trim())
    .map((d: any) => ({
      description: String(d.description),
      // Severity is nullable on purpose in the API (an unrecorded severity is
      // not a low one). The form's control has no empty option, so an absent
      // severity has to land somewhere; 'low' is the least alarming and the
      // reviewer can see the row to correct it. Flagged rather than hidden:
      // this is the one place the inverse cannot be exact.
      severity: (['low', 'medium', 'critical'].includes(d.severity) ? d.severity : 'low'),
    }));
}

/**
 * The fields common to every structured type, in `generalInfo`'s vocabulary.
 *
 * Callers spread this over the auto-populated seed rather than replacing it, so
 * a field the row does not carry keeps whatever the equipment record supplied.
 */
export function resumeGeneralInfo(row: InspectionRow): Record<string, string> {
  const out: Record<string, string> = {};
  const set = (k: string, v: string) => { if (v !== '') out[k] = v; };

  set('tongMake', str(pick(row, 'tong_make', 'tongMake')));
  set('tongModel', str(pick(row, 'tong_model', 'tongModel')));
  set('serialNumber', str(pick(row, 'serial_number', 'serialNumber')));
  set('jobLocation', str(pick(row, 'job_location', 'jobLocation')));
  set('operator', str(pick(row, 'operator_name', 'operatorName')));
  set('date', calendarDay(pick(row, 'inspection_date', 'inspectionDate')));
  set('time', clockTime(pick(row, 'inspection_time', 'inspectionTime')));

  // CRTi's own half. `toolModel` / `toolSerialNumber` are the CRTi labels for
  // the same two columns tong_model / serial_number already carry — the forward
  // map sends them there and deliberately does NOT send the duplicate keys, so
  // the inverse reads them back out of the same place.
  set('toolModel', str(pick(row, 'tong_model', 'tongModel')));
  set('toolSerialNumber', str(pick(row, 'serial_number', 'serialNumber')));
  set('location', str(pick(row, 'job_location', 'jobLocation')));
  set('inspectorName', str(pick(row, 'operator_name', 'operatorName')));
  set('rigName', str(pick(row, 'rig_name', 'rigName')));
  set('wellName', str(pick(row, 'well_name', 'wellName')));
  set('supervisor', str(pick(row, 'supervisor')));
  set('jobType', str(pick(row, 'job_type', 'jobType')));
  set('pipeSize', str(pick(row, 'pipe_size', 'pipeSize')));
  set('pipeWeight', str(pick(row, 'pipe_weight', 'pipeWeight')));
  set('meterageSinceLastInspection', numeric(pick(row, 'meterage_since_last_inspection', 'meterageSinceLastInspection')));
  set('totalLifetimeMeterage', numeric(pick(row, 'total_lifetime_meterage', 'totalLifetimeMeterage')));

  return out;
}

/** Elevators' own state object. `form_details` holds the tool attributes (0309). */
export function resumeElevatorInfo(row: InspectionRow): Record<string, string> {
  const d = (pick(row, 'form_details', 'formDetails') ?? {}) as Record<string, any>;
  const out: Record<string, string> = {};
  const set = (k: string, v: string) => { if (v !== '') out[k] = v; };

  set('inspectionType', str(d.inspection_type));
  set('elevatorManufacturer', str(d.elevator_manufacturer));
  set('elevatorModel', str(d.elevator_model, pick(row, 'tong_model', 'tongModel')));
  set('elevatorSize', str(d.elevator_size));
  set('ratedCapacity', str(d.rated_capacity));
  set('serialNumber', str(pick(row, 'serial_number', 'serialNumber')));
  set('assetNumber', str(d.asset_number));
  // `rig`, not `rig_name`: the state key is `rig` and the column is `rig_name`.
  // Posting the state key was exactly the bug 0305 could not map.
  set('rig', str(pick(row, 'rig_name', 'rigName')));
  set('wellName', str(pick(row, 'well_name', 'wellName')));
  set('location', str(pick(row, 'job_location', 'jobLocation')));

  return out;
}

/** Slip Elevator L1's own state object. */
export function resumeSlipElevatorInfo(row: InspectionRow): Record<string, string> {
  const d = (pick(row, 'form_details', 'formDetails') ?? {}) as Record<string, any>;
  const out: Record<string, string> = {};
  const set = (k: string, v: string) => { if (v !== '') out[k] = v; };

  set('elevatorType', str(d.elevator_type));
  set('manufacturer', str(d.elevator_manufacturer));
  set('model', str(d.elevator_model, pick(row, 'tong_model', 'tongModel')));
  set('size', str(d.elevator_size));
  set('ratedCapacity', str(d.rated_capacity));
  set('serialNumber', str(pick(row, 'serial_number', 'serialNumber')));
  set('assetNumber', str(d.asset_number));
  set('rig', str(pick(row, 'rig_name', 'rigName')));
  set('wellName', str(pick(row, 'well_name', 'wellName')));
  set('location', str(pick(row, 'job_location', 'jobLocation')));

  return out;
}

/** Rotary Slips L1's own state object. */
export function resumeRotarySlipsInfo(row: InspectionRow): Record<string, string> {
  const d = (pick(row, 'form_details', 'formDetails') ?? {}) as Record<string, any>;
  const out: Record<string, string> = {};
  const set = (k: string, v: string) => { if (v !== '') out[k] = v; };

  set('slipType', str(d.slip_type, pick(row, 'tong_model', 'tongModel')));
  set('manufacturer', str(d.slip_manufacturer));
  set('size', str(d.slip_size));
  set('assetNumber', str(d.asset_number));
  set('serialNumber', str(pick(row, 'serial_number', 'serialNumber')));
  set('rig', str(pick(row, 'rig_name', 'rigName')));

  return out;
}

/**
 * The two free-text boxes the Tong Truck checklist folds into one `notes`
 * column, pulled back apart.
 *
 * The forward direction joins at most two prefixed segments with a blank line.
 * Anything that does not carry a prefix is put in `issuesFound` rather than
 * dropped: `notes` is also written by the structured forms and by the AI agent,
 * and losing an inspector's sentence because it did not match a format is worse
 * than showing it in the nearer of the two boxes.
 */
export function resumeTongTruckNotes(row: InspectionRow): {
  issuesFound: string;
  correctiveActions: string;
} {
  const notes = str(pick(row, 'notes'));
  if (!notes) return { issuesFound: '', correctiveActions: '' };

  const ISSUES = 'Issues found: ';
  const ACTIONS = 'Corrective actions: ';

  let issuesFound = '';
  let correctiveActions = '';
  const leftovers: string[] = [];

  for (const segment of notes.split('\n\n')) {
    if (segment.startsWith(ISSUES)) issuesFound = segment.slice(ISSUES.length);
    else if (segment.startsWith(ACTIONS)) correctiveActions = segment.slice(ACTIONS.length);
    else leftovers.push(segment);
  }

  if (leftovers.length) {
    issuesFound = [issuesFound, ...leftovers].filter(Boolean).join('\n\n');
  }

  return { issuesFound, correctiveActions };
}

/**
 * The Tong Truck checklist rows, answers restored onto the CURRENT item list.
 *
 * Keyed by position (`1_${idx}`), which is how the forward map writes them, so
 * the caller passes the freshly-built item list and this fills in the answers.
 * A stored key with no matching row is dropped rather than appended — the
 * checklist is defined in code, and a row that is no longer on the form is no
 * longer a question anyone can answer.
 */
export function resumeTongTruckItems<T extends { status: string; notes: string }>(
  row: InspectionRow,
  items: T[],
): T[] {
  const results = resumeItemResults(row);
  return items.map((item, idx) => {
    const stored = results[`1_${idx}`];
    if (!stored) return item;
    return { ...item, status: stored.result, notes: stored.comment };
  });
}

/**
 * `overall_result` back into the Tong Truck checklist's radio value.
 *
 * The forward map sends 'conditional' for the checklist's middle option because
 * `overall_result` carries a CHECK constraint of pass / fail / conditional,
 * while the radio's own value is 'minor'. Restoring 'conditional' verbatim
 * would leave all three radios unselected, which reads as an unanswered
 * question on a form that had been answered.
 */
export function resumeTongTruckOverall(row: InspectionRow): string {
  const stored = str(pick(row, 'overall_result', 'overallResult'));
  return stored === 'conditional' ? 'minor' : stored;
}

/**
 * A DATE column as the calendar day it is, with no timezone in the path.
 *
 * `inspection_date` is a calendar day. pg serialises it `2026-08-28`, but a
 * driver or a JSON round trip can hand back a full ISO instant, and
 * `new Date('2026-08-28')` is UTC midnight — the 27th anywhere west of
 * Greenwich. Splitting the string never converts anything.
 */
function calendarDay(value: unknown): string {
  if (!value) return '';
  if (value instanceof Date) {
    // A driver-parsed DATE arrives at local midnight; read the local parts.
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return calendarDayString(String(value)) ?? String(value).slice(0, 10);
}

/** A TIME column as `HH:MM`, which is what the form's `type="time"` input reads. */
function clockTime(value: unknown): string {
  if (!value) return '';
  return String(value).slice(0, 5);
}

/**
 * A NUMERIC column as the string its text input holds.
 *
 * pg returns NUMERIC as a string to preserve precision, and it carries the
 * column's scale — `1450` stored in NUMERIC(12,2) comes back `"1450.00"`.
 * Restoring that verbatim would put "1450.00" in a box the inspector typed
 * "1450" into, and it would then save back changed. Trailing zeros after a
 * decimal point are trimmed; the value itself is never rounded or reformatted.
 */
function numeric(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const s = String(value);
  if (!/^-?\d+\.\d+$/.test(s)) return s;
  return s.replace(/\.?0+$/, '');
}
